/**
 * BoqInlineEntryRow Component
 * Renders an inline editable table row for a BOQ entry.
 * Eliminates disruptive pop-up dialogs by allowing designers to edit
 * description, room, quantity, and unit price directly in the table.
 */

import { Check, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { paisaToRupeeInput, rupeeInputToPaisa } from "@/lib/format";
import {
	type BOQEntry,
	boqEntryName,
	formatCurrency,
	updateBOQEntry,
} from "../../services/boq/boq-api.service";

interface BoqInlineEntryRowProps {
	boqId: string;
	entry: BOQEntry;
	currencyCode: string;
	onSaved: () => void;
	onCancel: () => void;
}

export function BoqInlineEntryRow({
	boqId,
	entry,
	currencyCode,
	onSaved,
	onCancel,
}: BoqInlineEntryRowProps) {
	const [description, setDescription] = useState(entry.description || "");
	const [quantity, setQuantity] = useState(entry.quantity.toString());
	const [unitPrice, setUnitPrice] = useState(
		paisaToRupeeInput(entry.unitPrice),
	);
	const [roomName, setRoomName] = useState(
		entry.roomCustomName || entry.roomName || "",
	);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const descriptionInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		descriptionInputRef.current?.focus();
	}, []);

	// Calculate dynamic live line total
	const parsedQty = Number.parseFloat(quantity);
	const safeQty = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 0;
	const parsedPaisa = rupeeInputToPaisa(unitPrice);
	const liveTotalPaisa = Math.round(safeQty * parsedPaisa);

	const handleSave = async () => {
		if (safeQty <= 0) {
			toast.error("Quantity must be greater than 0");
			return;
		}

		const trimmedDesc = description.trim();
		const trimmedRoom = roomName.trim();

		const changes = {
			description:
				trimmedDesc !== (entry.description ?? "") ? trimmedDesc : undefined,
			quantity: safeQty !== entry.quantity ? safeQty : undefined,
			unitPrice: parsedPaisa !== entry.unitPrice ? parsedPaisa : undefined,
			roomCustomName:
				trimmedRoom !== (entry.roomCustomName ?? entry.roomName ?? "")
					? trimmedRoom
					: undefined,
		};

		if (Object.values(changes).every((v) => v === undefined)) {
			onCancel();
			return;
		}

		setIsSubmitting(true);
		try {
			const result = await updateBOQEntry(
				boqId,
				entry.boqSectionId,
				entry.id,
				changes,
			);

			if (result.success) {
				toast.success("Entry updated");
				onSaved();
			} else {
				toast.error(result.error || "Failed to update entry");
			}
		} catch {
			toast.error("Failed to update entry");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSave();
		} else if (e.key === "Escape") {
			e.preventDefault();
			onCancel();
		}
	};

	return (
		<tr
			style={{
				background:
					"color-mix(in srgb, var(--color-dashboard-accent) 6%, var(--color-dashboard-paper))",
				borderBottom: "1px solid var(--color-dashboard-accent)",
			}}
			onKeyDown={handleKeyDown}
		>
			{/* Line # */}
			<td
				style={{
					fontFamily: "var(--font-mono)",
					fontSize: 11,
					color: "var(--color-dashboard-placeholder)",
					padding: "8px 14px",
					verticalAlign: "middle",
				}}
			>
				{entry.lineNo}
			</td>

			{/* Product Name */}
			<td
				style={{
					fontFamily: "var(--font-body)",
					fontSize: 13,
					fontWeight: 500,
					color: "var(--color-dashboard-text)",
					padding: "8px 14px",
					verticalAlign: "middle",
				}}
			>
				{boqEntryName(entry)}
			</td>

			{/* Description Input */}
			<td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
				<input
					ref={descriptionInputRef}
					type="text"
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					placeholder="Specification / description"
					aria-label="Description"
					style={{
						width: "100%",
						padding: "6px 10px",
						fontSize: 13,
						fontFamily: "var(--font-body)",
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
						borderRadius: 6,
						color: "var(--color-dashboard-text)",
						outline: "none",
					}}
				/>
			</td>

			{/* Room Input */}
			<td style={{ padding: "8px 10px", width: 110, verticalAlign: "middle" }}>
				<input
					type="text"
					value={roomName}
					onChange={(e) => setRoomName(e.target.value)}
					placeholder="Room / space"
					aria-label="Room"
					style={{
						width: "100%",
						padding: "6px 10px",
						fontSize: 12,
						fontFamily: "var(--font-body)",
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
						borderRadius: 6,
						color: "var(--color-dashboard-text)",
						outline: "none",
					}}
				/>
			</td>

			{/* UoM (Unit) */}
			<td
				style={{
					fontFamily: "var(--font-mono)",
					fontSize: 11,
					color: "var(--color-dashboard-muted-dark)",
					padding: "8px 14px",
					textAlign: "right",
					verticalAlign: "middle",
				}}
			>
				{entry.uom || "—"}
			</td>

			{/* Qty Input */}
			<td style={{ padding: "8px 10px", width: 85, verticalAlign: "middle" }}>
				<input
					type="number"
					step="any"
					min="0.01"
					value={quantity}
					onChange={(e) => setQuantity(e.target.value)}
					aria-label="Quantity"
					style={{
						width: "100%",
						textAlign: "right",
						padding: "6px 8px",
						fontSize: 12,
						fontFamily: "var(--font-mono)",
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
						borderRadius: 6,
						color: "var(--color-dashboard-text)",
						outline: "none",
					}}
				/>
			</td>

			{/* Unit Price (₹) Input */}
			<td style={{ padding: "8px 10px", width: 120, verticalAlign: "middle" }}>
				<div
					style={{
						position: "relative",
						display: "flex",
						alignItems: "center",
					}}
				>
					<span
						style={{
							position: "absolute",
							left: 8,
							fontSize: 11,
							color: "var(--color-dashboard-muted)",
							pointerEvents: "none",
						}}
					>
						₹
					</span>
					<input
						type="number"
						step="any"
						min="0"
						value={unitPrice}
						onChange={(e) => setUnitPrice(e.target.value)}
						aria-label="Unit price"
						style={{
							width: "100%",
							textAlign: "right",
							padding: "6px 8px 6px 20px",
							fontSize: 12,
							fontFamily: "var(--font-mono)",
							background: "var(--color-dashboard-paper)",
							border: "1px solid var(--color-dashboard-border)",
							borderRadius: 6,
							color: "var(--color-dashboard-text)",
							outline: "none",
						}}
					/>
				</div>
			</td>

			{/* Dynamic Live Line Total */}
			<td
				style={{
					fontFamily: "var(--font-display)",
					fontSize: 13,
					fontWeight: 600,
					color: "var(--color-dashboard-accent-dark)",
					padding: "8px 14px",
					textAlign: "right",
					verticalAlign: "middle",
				}}
			>
				{formatCurrency(liveTotalPaisa, currencyCode)}
			</td>

			{/* Approved Status Column */}
			<td
				style={{
					padding: "8px 14px",
					textAlign: "center",
					verticalAlign: "middle",
				}}
			>
				<span style={{ fontSize: 11, color: "var(--color-dashboard-muted)" }}>
					Editing
				</span>
			</td>

			{/* Actions: Save (Check) & Cancel (X) */}
			<td
				style={{
					padding: "8px 14px",
					textAlign: "right",
					verticalAlign: "middle",
				}}
			>
				<div style={{ display: "flex", justifyContent: "flex-end", gap: 6 }}>
					<button
						type="button"
						onClick={handleSave}
						disabled={isSubmitting}
						title="Save changes (Enter)"
						aria-label="Save changes"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 28,
							height: 28,
							borderRadius: 5,
							background: "var(--color-dashboard-accent)",
							color: "var(--color-dashboard-on-fill)",
							border: "none",
							cursor: isSubmitting ? "not-allowed" : "pointer",
							opacity: isSubmitting ? 0.7 : 1,
						}}
					>
						{isSubmitting ? (
							<Loader2 size={13} className="animate-spin" />
						) : (
							<Check size={13} />
						)}
					</button>
					<button
						type="button"
						onClick={onCancel}
						disabled={isSubmitting}
						title="Cancel (Esc)"
						aria-label="Cancel editing"
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 28,
							height: 28,
							borderRadius: 5,
							background: "transparent",
							border: "1px solid var(--color-dashboard-border)",
							color: "var(--color-dashboard-muted)",
							cursor: "pointer",
						}}
					>
						<X size={13} />
					</button>
				</div>
			</td>
		</tr>
	);
}
